import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

document.addEventListener("DOMContentLoaded", () => {
  function initImpactGrid() {
    const impactGrid = document.querySelector<HTMLElement>(".impact-grid-list");

    if (!impactGrid) return;

    const impactGridItems =
      impactGrid.querySelectorAll<HTMLElement>(".impact-grid-item");

    impactGridItems.forEach((item, idx) => {
      const h3 = item.querySelector<HTMLElement>("h3");
      const p = item.querySelector<HTMLElement>("p");

      gsap.set([h3, p], { autoAlpha: 0 });
      const staggerDelay = (idx > 2 ? idx % 3 : idx) * 0.3;

      ScrollTrigger.create({
        trigger: item,
        start: "top 80%",
        onEnter: () => {
          gsap.to([h3, p], {
            autoAlpha: 1,
            duration: 0.5,
            delay: staggerDelay,
          });
        },
      });
    });
  }
  initImpactGrid();

  function initMainHero() {
    const hero = document.querySelector<HTMLElement>(".hero");

    if (!hero) return;

    const heroImg = hero.querySelector<HTMLElement>(".hero-img");

    gsap.set(heroImg, { y: 25, autoAlpha: 0 });

    ScrollTrigger.create({
      trigger: heroImg,
      start: "top 50%",
      onEnter: () => {
        gsap.to(heroImg, { y: 0, autoAlpha: 1, duration: 0.75 });
      },
    });
  }
  initMainHero();

  function initIntro() {
    const intro = document.querySelector<HTMLElement>(".intro");

    if (!intro) return;

    const introImg = intro.querySelector<HTMLElement>(".intro-img");

    gsap.fromTo(
      introImg,
      { y: 25, autoAlpha: 0 },
      { y: 0, autoAlpha: 1, duration: 0.75 }
    );
  }
  initIntro();

  function initDualCTA() {
    const dualCTA = document.querySelector<HTMLElement>(
      ".wp-block-ds-dual-call-to-action"
    );

    if (!dualCTA) return;

    const dualCTAItems = dualCTA.querySelectorAll<HTMLElement>(
      ".dual-call-to-action"
    );

    dualCTAItems.forEach((item) => {
      item.addEventListener("mouseenter", () => {
        gsap.to(item, { y: -20, duration: 0.25 });
      });

      item.addEventListener("mouseleave", () => {
        gsap.to(item, { y: -23, duration: 0.1 });
        gsap.to(item, { y: 0, duration: 0.25, delay: 0.1 });
      });
    });
  }
  initDualCTA();

  function initCapitalCTA() {
    const capitalCTA = document.querySelector<HTMLElement>(".call-to-action");

    if (!capitalCTA) return;

    ScrollTrigger.create({
      trigger: capitalCTA,
      start: "top 50%",
      onEnter: () => {
        gsap.to(capitalCTA, {
          "--width": "0",
          "--height": "0",
        });
      },
    });
  }
  initCapitalCTA();

  function initCapitalHero() {
    const capitalHero = document.querySelector<HTMLElement>(".impact-hero");

    if (!capitalHero) return;

    const capitalHeroDiamondTwo = capitalHero.querySelector<HTMLElement>(
      ".impact-hero-diamond-two"
    );

    ScrollTrigger.create({
      trigger: capitalHeroDiamondTwo,
      start: "top 50%",
      onEnter: () => {
        capitalHeroDiamondTwo &&
          capitalHeroDiamondTwo.classList.add("is-active");
      },
    });
  }
  initCapitalHero();

  // Collective Hero animations
  const heros = document.querySelectorAll(
    ".wp-block-ds-collective-hero, .collective-page .call-to-action, .spring_collective .call-to-action"
  );
  const observerOptions = {
    threshold: [0.6],
  };
  const heroScroller = new IntersectionObserver(
    heroIntersectionCallback,
    observerOptions
  );

  function heroIntersectionCallback(entries) {
    entries.forEach((el) => {
      if (el.isIntersecting) {
        const gridWrapper = el.target.querySelector(".grid-wrapper");
        const hero = el.target.querySelector(".collective-hero-bg");
        const heading = el.target.querySelector(".heading-xl");

        if (el.intersectionRatio >= 0.25) {
          gsap.fromTo(
            gridWrapper,
            { scale: 0 },
            {
              scale: 1,
              duration: 0.6,
              ease: "sine.out",
              transformOrigin: "left bottom",
            }
          );
          gsap.fromTo(
            hero,
            { scale: 0 },
            {
              scale: 1,
              duration: 0.3,
              delay: 0.2,
              ease: "sine.out",
              transformOrigin: "left bottom",
            }
          );
          gsap.fromTo(
            heading,
            { opacity: 0 },
            {
              delay: 0.8,
              opacity: 1,
              duration: 0.6,
              ease: "sine.out",
              transformOrigin: "left",
            }
          );
          heroScroller.unobserve(el.target);
        }
      }
    });
  }

  heros.forEach((hero) => {
    heroScroller.observe(hero);
  });

  // Collective CTA animations
  const collectiveCTAs = document.querySelectorAll(
    ".collective-page .call-to-action, .spring_collective .call-to-action"
  );

  const CTAObserverOptions = {
    threshold: [0.4],
  };

  const CTAScroller = new IntersectionObserver(
    ctaIntersectionCallback,
    CTAObserverOptions
  );

  function ctaIntersectionCallback(entries) {
    entries.forEach((el) => {
      if (el.isIntersecting) {
        const gridWrapper = el.target.querySelector(".grid-wrapper");
        const hero = el.target.querySelector(".call-to-action-diamond");
        const heading = el.target.querySelector(".heading-l");
        const subtitle = el.target.querySelector(".call-to-action-subtitle");
        const cta = el.target.querySelector(".call-to-action-button");

        if (subtitle) {
          gsap.fromTo(
            subtitle,
            { opacity: 0 },
            {
              delay: 0.8,
              opacity: 1,
              duration: 0.6,
              ease: "sine.out",
              transformOrigin: "left",
            }
          );
        }
        if (cta) {
          gsap.fromTo(
            cta,
            { opacity: 0 },
            {
              delay: 0.8,
              opacity: 1,
              duration: 0.6,
              ease: "sine.out",
              transformOrigin: "left",
            }
          );
        }
        gsap.fromTo(
          gridWrapper,
          { scale: 0 },
          {
            scale: 1,
            duration: 0.6,
            ease: "sine.out",
            transformOrigin: "left bottom",
          }
        );
        gsap.fromTo(
          hero,
          { scale: 0 },
          {
            scale: 1,
            duration: 0.3,
            delay: 0.2,
            ease: "sine.out",
            transformOrigin: "left bottom",
          }
        );
        gsap.fromTo(
          heading,
          { opacity: 0 },
          {
            delay: 0.8,
            opacity: 1,
            duration: 0.6,
            ease: "sine.out",
            transformOrigin: "left",
          }
        );
        CTAScroller.unobserve(el.target);
      }
    });
  }

  collectiveCTAs.forEach((cta) => {
    CTAScroller.observe(cta);
  });

  // Inverting main CTA colors on load
  const observerOptionsCTAs = {
    threshold: [0.5],
  };
  const springMainCTAs = document.querySelectorAll(
    ".spring_main .call-to-action, .spring_usa .call-to-action"
  );
  const mainCTAsScroller = new IntersectionObserver(
    crossfadeCTAcolors,
    observerOptionsCTAs
  );

  function crossfadeCTAcolors(entries) {
    entries.forEach((el) => {
      if (el.isIntersecting) {
        el.target.classList.add("invert-colors");
        mainCTAsScroller.unobserve(el.target);
      }
    });
  }

  springMainCTAs.forEach((mainCTAs) => {
    mainCTAsScroller.observe(mainCTAs);
  });

  // Animate borders and H2s on load
  const H2s = document.querySelectorAll(
    "H2.wp-block-heading, .logo-wall-heading, .featured-content-head, .media-links-heading, .people-header-container, .portfolio-header-container"
  );
  const bordersScroller = new IntersectionObserver(animateH2, observerOptions);

  function animateH2(entries) {
    // if on impact report page, don't animate borders
    if (window.location.href.includes("/impact-report/")) return;

    entries.forEach((el) => {
      if (el.isIntersecting) {
        const tl = gsap.timeline();
        // tl.fromTo(
        //   el.target,
        //   {
        //     scaleY: 0,
        //     transformOrigin: "bottom",
        //     duration: 0.25,
        //     immediateRender: false,
        //     autoRound: false,
        //     ease: "sine.out",
        //   },
        //   {
        //     scaleY: "100%",
        //   }
        // );
        el.target.classList.add("animated-heading");
        tl.to(el.target, {
          width: "100%",
          duration: 0.25,
          immediateRender: false,
          autoRound: false,
          ease: "sine.out",
        });
        bordersScroller.unobserve(el.target);
      }
    });
  }

  H2s.forEach((H2) => {
    bordersScroller.observe(H2);
  });
});
